import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { createHttpParamsFromRecord } from '../secure/common/helpers';
import { HasItems, ReviewManagementBranch } from '../types';
import {
  CreateLocationGroupRequest,
  GetLocationGroupByIdRequest,
  GetLocationGroupsUsageRequest,
  LocationGroup,
  LocationGroupWithLocations,
  PatchLocationGroupRequest,
  UpdateLocationGroupWhitelistRequest,
} from '../types/location-group-response';

@Injectable({
  providedIn: 'root',
})
export class LocationGroupService {
  private httpClient = inject(HttpClient);
  readonly apiHost = `${environment.apiBaseUrl}`;

  // scoped to logged user (currently no query params supported)
  getAllLocationGroups(): Observable<HasItems<LocationGroup>> {
    const requestUrl = `${this.apiHost}/LocationGroups`;
    return this.httpClient.get<HasItems<LocationGroup>>(requestUrl);
  }

  // For now, we can include locations but the nested "cameras" will always be empty because
  // it's not implemented to also retrieve the cameras for each location in the group, nor the incidents count.
  // If that information is needed, use the "get locations" method in location.service.ts
  getLocationGroupById(request: GetLocationGroupByIdRequest): Observable<LocationGroupWithLocations> {
    const requestUrl = `${this.apiHost}/LocationGroups/${request.location_group_id}`;
    const params = createHttpParamsFromRecord({
      include_locations: request.include_locations ? 'true' : 'false',
    });
    return this.httpClient.get<LocationGroupWithLocations>(requestUrl, { params });
  }

  // only includes locations from branches where the user is branch owner, branch manager or market manager
  getLocationGroupsUsage(request: GetLocationGroupsUsageRequest): Observable<ReviewManagementBranch> {
    const requestUrl = `${this.apiHost}/LocationGroups/Usage`;
    const params = createHttpParamsFromRecord({
      location_group_ids: request.location_group_ids,
      branch_ids: (request.branch_ids ?? []).map(String),
    });
    return this.httpClient.get<ReviewManagementBranch>(requestUrl, { params });
  }

  updateLocationGroupWhitelist(locationGroupId: string, request: UpdateLocationGroupWhitelistRequest): Observable<LocationGroup> {
    const requestUrl = `${this.apiHost}/LocationGroups/${locationGroupId}/DomainWhitelist`;
    return this.httpClient.put<LocationGroup>(requestUrl, request);
  }

  createLocationGroup(locationGroup: CreateLocationGroupRequest): Observable<LocationGroup> {
    const requestUrl = `${this.apiHost}/LocationGroups`;
    return this.httpClient.post<LocationGroup>(requestUrl, locationGroup);
  }

  // null values are ignored, empty string means "clear it". Also, empty name is invalid.
  patchLocationGroup(locationGroupId: string, request: PatchLocationGroupRequest): Observable<LocationGroup> {
    const requestUrl = `${this.apiHost}/LocationGroups/${locationGroupId}`;
    return this.httpClient.patch<LocationGroup>(requestUrl, request);
  }

  addLocationToGroup(locationGroupId: string, locationId: number): Observable<LocationGroup> {
    const requestUrl = `${this.apiHost}/LocationGroups/${locationGroupId}/Locations/${locationId}`;
    return this.httpClient.put<LocationGroup>(requestUrl, {});
  }

  removeLocationFromGroup(locationGroupId: string, locationId: number): Observable<LocationGroup> {
    const requestUrl = `${this.apiHost}/LocationGroups/${locationGroupId}/Locations/${locationId}`;
    return this.httpClient.delete<LocationGroup>(requestUrl);
  }
}
